import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useRef, useState, type ChangeEvent, type DragEvent } from 'react';
import { useTranslation } from 'react-i18next';
import type { PlateRecognitionDto } from '@parking/shared';
import { api } from '../shared/api/client';
import { useAuthStore } from '../shared/store/authStore';
import { PageHeader } from '../shared/ui/PageHeader';
import { Card } from '../shared/ui/Card';
import { ParkingDataTable } from '../shared/ui/ParkingDataTable';
import { AppModal } from '../shared/ui/AppModal';
import { StatusBadge } from '../shared/ui/StatusBadge';

export function MatriculasPage() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const canRead = useAuthStore(
    (s) => s.hasPermission('sessions:read') || s.hasPermission('api:access'),
  );
  const canWrite = useAuthStore(
    (s) => s.hasPermission('sessions:write') || s.hasPermission('api:access'),
  );

  const list = useQuery({
    queryKey: ['matriculas'],
    queryFn: () => api.listMatriculas(),
    enabled: canRead,
  });

  const [open, setOpen] = useState(false);
  const [preview, setPreview] = useState<string | null>(null);
  const [imageBase64, setImageBase64] = useState<string | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [detail, setDetail] = useState<PlateRecognitionDto | null>(null);
  const [dragging, setDragging] = useState(false);

  const recognize = useMutation({
    mutationFn: () => {
      if (!imageBase64) throw new Error(t('matriculas.needImage'));
      return api.recognizeMatricula({ imageBase64 });
    },
    onSuccess: async (res) => {
      setOpen(false);
      setPreview(null);
      setImageBase64(null);
      setFormError(null);
      setDragging(false);
      await qc.invalidateQueries({ queryKey: ['matriculas'] });
      setDetail(res.data);
    },
    onError: (e: Error) => setFormError(e.message),
  });

  function acceptImageFile(file: File | undefined | null) {
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      setFormError(t('matriculas.invalidImage'));
      return;
    }
    if (file.size > 6_000_000) {
      setFormError(t('matriculas.imageTooLarge'));
      return;
    }
    setFormError(null);
    const reader = new FileReader();
    reader.onload = () => {
      const result = typeof reader.result === 'string' ? reader.result : null;
      setPreview(result);
      setImageBase64(result);
    };
    reader.onerror = () => setFormError(t('matriculas.invalidImage'));
    reader.readAsDataURL(file);
  }

  function onFile(e: ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = '';
    acceptImageFile(file);
  }

  function onDragOver(e: DragEvent<HTMLDivElement>) {
    e.preventDefault();
    e.stopPropagation();
    if (recognize.isPending) return;
    setDragging(true);
  }

  function onDragLeave(e: DragEvent<HTMLDivElement>) {
    e.preventDefault();
    e.stopPropagation();
    setDragging(false);
  }

  function onDrop(e: DragEvent<HTMLDivElement>) {
    e.preventDefault();
    e.stopPropagation();
    setDragging(false);
    if (recognize.isPending) return;
    const file = e.dataTransfer.files?.[0];
    acceptImageFile(file);
  }

  if (!canRead) {
    return <div className="alert alert-warning">{t('matriculas.forbidden')}</div>;
  }

  const rows = list.data?.data ?? [];
  const columns = [
    t('matriculas.image'),
    t('matriculas.plate'),
    t('matriculas.confidence'),
    t('matriculas.model'),
    t('matriculas.status'),
    t('matriculas.when'),
    t('common.actions'),
  ];

  function statusTone(status: string): 'success' | 'warning' | 'danger' | 'secondary' {
    if (status === 'ok') return 'success';
    if (status === 'no_plate') return 'warning';
    if (status === 'error') return 'danger';
    return 'secondary';
  }

  function thumb(url: string | null) {
    if (!url) return <span className="text-muted">—</span>;
    return (
      <img
        src={url}
        alt=""
        style={{
          width: 88,
          height: 52,
          objectFit: 'cover',
          borderRadius: 6,
          border: '1px solid #e4e6ef',
        }}
      />
    );
  }

  return (
    <>
      <PageHeader
        actions={
          canWrite ? (
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => {
                setFormError(null);
                setPreview(null);
                setImageBase64(null);
                setOpen(true);
              }}
            >
              {t('matriculas.read')}
            </button>
          ) : undefined
        }
      />

      {list.isError && (
        <div className="alert alert-danger">{(list.error as Error).message}</div>
      )}

      <Card>
        <ParkingDataTable
          storageKey="matriculas"
          columns={columns}
          refreshKey={rows.map((r) => `${r.id}:${r.status}`).join('|')}
          actionsColumn
          galleryItems={rows.map((r) => ({
            id: r.id,
            icon: 'bi-card-heading',
            iconTone: statusTone(r.status),
            title: (
              <div className="d-flex align-items-center gap-2">
                {thumb(r.imageUrl)}
                <span className="fw-semibold">{r.plateText ?? '—'}</span>
              </div>
            ),
            subtitle: r.model,
            meta: `${r.confidence != null ? `${Math.round(r.confidence * 100)}%` : '—'} · ${new Date(r.createdAt).toLocaleString()}`,
            badge: (
              <StatusBadge tone={statusTone(r.status)}>{r.status}</StatusBadge>
            ),
            actions: (
              <button
                type="button"
                className="btn btn-sm btn-light"
                onClick={() => setDetail(r)}
              >
                {t('matriculas.detail')}
              </button>
            ),
          }))}
        >
          {rows.map((r) => (
            <tr key={r.id}>
              <td>{thumb(r.imageUrl)}</td>
              <td className="fw-semibold">{r.plateText ?? '—'}</td>
              <td>{r.confidence != null ? `${Math.round(r.confidence * 100)}%` : '—'}</td>
              <td className="small">{r.model}</td>
              <td>
                <StatusBadge tone={statusTone(r.status)}>{r.status}</StatusBadge>
              </td>
              <td className="small">{new Date(r.createdAt).toLocaleString()}</td>
              <td>
                <button
                  type="button"
                  className="btn btn-sm btn-light"
                  onClick={() => setDetail(r)}
                >
                  {t('matriculas.detail')}
                </button>
              </td>
            </tr>
          ))}
        </ParkingDataTable>
      </Card>

      <AppModal
        open={open}
        onClose={() => !recognize.isPending && setOpen(false)}
        title={t('matriculas.read')}
        footer={
          <>
            <button
              type="button"
              className="btn btn-light"
              disabled={recognize.isPending}
              onClick={() => setOpen(false)}
            >
              {t('common.cancel')}
            </button>
            <button
              type="button"
              className="btn btn-primary"
              disabled={!imageBase64 || recognize.isPending}
              onClick={() => recognize.mutate()}
            >
              {recognize.isPending ? t('matriculas.recognizing') : t('matriculas.send')}
            </button>
          </>
        }
      >
        <div className="d-grid gap-3">
          <div className="alert alert-info mb-0 small">{t('matriculas.openaiHint')}</div>
          {formError && <div className="alert alert-danger mb-0">{formError}</div>}
          <div>
            <label className="form-label">{t('matriculas.upload')}</label>
            <div
              className={`pk-dropzone${dragging ? ' is-dragging' : ''}${preview ? ' has-preview' : ''}`}
              onDragEnter={onDragOver}
              onDragOver={onDragOver}
              onDragLeave={onDragLeave}
              onDrop={onDrop}
              onClick={() => {
                if (!recognize.isPending) fileInputRef.current?.click();
              }}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  if (!recognize.isPending) fileInputRef.current?.click();
                }
              }}
              aria-label={t('matriculas.dropHint')}
            >
              <input
                ref={fileInputRef}
                type="file"
                className="d-none"
                accept="image/png,image/jpeg,image/webp"
                onChange={onFile}
                disabled={recognize.isPending}
              />
              {preview ? (
                <div className="pk-dropzone__preview">
                  <img src={preview} alt="" />
                  <div className="pk-dropzone__preview-actions">
                    <span className="small text-muted">{t('matriculas.dropReplace')}</span>
                    <button
                      type="button"
                      className="btn btn-sm btn-light"
                      disabled={recognize.isPending}
                      onClick={(e) => {
                        e.stopPropagation();
                        setPreview(null);
                        setImageBase64(null);
                        setFormError(null);
                      }}
                    >
                      {t('matriculas.clearImage')}
                    </button>
                  </div>
                </div>
              ) : (
                <div className="pk-dropzone__empty">
                  <i className="bi bi-cloud-arrow-up" aria-hidden />
                  <div className="pk-dropzone__title">{t('matriculas.dropTitle')}</div>
                  <div className="pk-dropzone__hint">{t('matriculas.dropHint')}</div>
                </div>
              )}
            </div>
          </div>
        </div>
      </AppModal>

      <AppModal
        open={Boolean(detail)}
        onClose={() => setDetail(null)}
        title={t('matriculas.detail')}
        footer={
          <button type="button" className="btn btn-light" onClick={() => setDetail(null)}>
            {t('common.cancel')}
          </button>
        }
      >
        {detail && (
          <div className="d-grid gap-3">
            {detail.imageUrl && (
              <div className="text-center">
                <img
                  src={detail.imageUrl}
                  alt=""
                  style={{ maxWidth: '100%', maxHeight: 220, borderRadius: 8 }}
                />
              </div>
            )}
            <div>
              <div className="text-muted small">{t('matriculas.plate')}</div>
              <div className="fw-semibold fs-4">{detail.plateText ?? '—'}</div>
            </div>
            {detail.errorMessage && (
              <div className="alert alert-warning mb-0">{detail.errorMessage}</div>
            )}
            <div>
              <div className="text-muted small mb-1">{t('matriculas.requestMeta')}</div>
              <pre
                className="bg-light border rounded p-3 small mb-0"
                style={{ maxHeight: 180, overflow: 'auto' }}
              >
                {JSON.stringify(detail.requestMeta, null, 2)}
              </pre>
            </div>
            <div>
              <div className="text-muted small mb-1">{t('matriculas.openaiResponse')}</div>
              <pre
                className="bg-light border rounded p-3 small mb-0"
                style={{ maxHeight: 240, overflow: 'auto' }}
              >
                {JSON.stringify(detail.openaiResponse, null, 2)}
              </pre>
            </div>
          </div>
        )}
      </AppModal>
    </>
  );
}
